import * as R from 'ramda'
import {arrayOf, bool, node as reactNode, shape, string} from 'prop-types'
import {StyledEngineProvider, ThemeProvider} from '@mui/material/styles'
import loadable from '@loadable/component'
import React, {useEffect, useState} from 'react'

import {getServices} from 'helpers/services'
import {
  isInsightArticlePathname,
  isKeyrusAiPathname,
  KEYRUS_AI_BANNER_HEIGHT_CSS_VAR,
} from 'helpers/keyrusAi'
import defaultTheme from 'config/theme'
import Footer from 'components/Layout/FooterKeyrus'
import Header from 'components/Layout/HeaderKeyrus'
import KeyrusAiGateway from 'components/KeyrusAiGateway'
import palette1 from 'config/palette1'
import useAosInit from 'hooks/useAosInit'
import useIsMobile from 'hooks/useIsMobile'
import useIsPartnersPage from 'hooks/useIsPartnersPage'
import useMountComponent from 'hooks/useMountComponent'

import useStyles from './styles'

const LayoutKeyrus = React.memo(props => {
  const Popper = loadable(() => import('components/UI/Popper'))
  const ScrollTop = loadable(() => import('components/UI/ScrollTopButton'))
  const isPartnersPage = useIsPartnersPage()
  const {
    children,
    data,
    location,
    pageHasNoNavigation,
    layoutData,
    pageContext,
  } = props
  const isMobile = useIsMobile()
  const countryData = R.path(['contentfulCountry'], data)
  const showNav =
    process.env.GATSBY_IN_SITE_MAINTENANCE === 'false' && !pageHasNoNavigation
  // The gateway must never be active on the /keyrus-ai landing itself — the
  // template already renders KeyrusAiLandingContent, so an active overlay would
  // render it twice. Decided here (SSR-correct via the location prop) on top of
  // the component's own pathname self-skip.
  const isKeyrusAiRoute = isKeyrusAiPathname(
    R.pathOr('', ['pathname'], location),
  )
  // Campaign deep links to insight articles / white papers should open the
  // content directly, so the gateway is suppressed on those detail routes.
  const isInsightArticleRoute = isInsightArticlePathname(
    R.pathOr('', ['pathname'], location),
  )

  const newCountryServices = R.path(['countryServices'], countryData)

  const services = showNav
    ? R.pathOr(null, ['contentfulCountry', 'services'], data)
    : null

  const topLevelServices =
    services && R.pathOr(null, ['topLevelServices'], services)

  const countryServices =
    showNav && topLevelServices ? getServices(data, topLevelServices) : null

  const classes = useStyles()

  const [showLayout, setShowLayout] = useState(false)

  const isLayoutMounted = useMountComponent()

  const localizedPopper =
    showNav &&
    layoutData.allContentfulRedirectionPopUp.edges.find(({node}) => {
      if (!node.countries) {
        return false
      }

      return node.countries.find(
        country =>
          country.technicalName === countryData.technicalName &&
          node.node_locale === pageContext.nodeLocale,
      )
    })

  const defaultPopperContent =
    showNav &&
    layoutData.allContentfulRedirectionPopUp.edges.find(
      ({node}) =>
        node.countries === null && node.node_locale === pageContext.nodeLocale,
    )

  const popperContent = showNav
    ? (localizedPopper && localizedPopper.node) ||
      (defaultPopperContent && defaultPopperContent.node)
    : null

  const [popperData, setPopperData] = useState(
    showNav && popperContent
      ? {
          link: '',
          text: popperContent.textContent.textContent,
          cta1: popperContent.cta1,
          cta2: popperContent.cta2,
          showPopper: false,
        }
      : {
          cta1: null,
          cta2: null,
          link: '',
          showPopper: false,
          text: '',
        },
  )

  const getLayoutMarginTop = () => {
    if (!showNav) {
      return 0
    }

    return `calc(${
      isMobile ? 25 : 100
    }px + var(${KEYRUS_AI_BANNER_HEIGHT_CSS_VAR}, 0px))`
  }

  const handleLayoutDisplay = shouldDisplay =>
    shouldDisplay
      ? {visibility: 'visible', marginTop: getLayoutMarginTop()}
      : {visibility: 'hidden'}

  useEffect(() => {
    setShowLayout(isLayoutMounted)
  }, [isLayoutMounted])

  useAosInit(showLayout)

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={defaultTheme}>
        <ThemeProvider theme={palette1}>
          {showNav && (
            <Popper
              showPopper={popperData.showPopper}
              setShowPopper={() =>
                setPopperData({...popperData, showPopper: false})
              }
              link={popperData.link}
              label={popperData.text}
              cta1={popperData.cta1}
              cta2={popperData.cta2}
              showLink={false}
              shouldOpenInNewTab={false}
            />
          )}
          <KeyrusAiGateway
            countryData={countryData}
            enabled={
              showNav &&
              Boolean(countryData) &&
              !isKeyrusAiRoute &&
              !isInsightArticleRoute
            }
            isMobile={isMobile}
            modalDisabled
            nodeLocale={pageContext.nodeLocale}
          />
          <div
            style={handleLayoutDisplay(showLayout)}
            className={showNav ? classes.wrapper : undefined}
          >
            {showNav && (
              <Header
                pageContext={pageContext}
                countryData={countryData}
                layoutData={layoutData}
                pageData={data}
                popperData={popperData}
                setPopperData={setPopperData}
                countryServices={countryServices}
                newCountryServices={newCountryServices}
              />
            )}
            {children}
            {showNav && (
              <Footer
                pageContext={pageContext}
                countryData={countryData}
                data={layoutData}
              />
            )}
            {showNav && !isPartnersPage && <ScrollTop />}
          </div>
        </ThemeProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  )
})

LayoutKeyrus.propTypes = {
  children: reactNode.isRequired,
  data: shape({}).isRequired,
  layoutData: shape({
    allContentfulRedirectionPopUp: shape({
      edges: arrayOf(
        shape({
          node: shape({
            cta1: string,
            cta2: string,
            node_locale: string,
            textContent: shape({
              textContent: string,
            }),
          }),
        }),
      ),
    }),
  }).isRequired,
  location: shape({
    pathname: string,
  }),
  pageContext: shape({
    nodeLocale: string,
  }).isRequired,
  pageHasNoNavigation: bool.isRequired,
}

LayoutKeyrus.defaultProps = {
  location: null,
}

export default LayoutKeyrus
